import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import React from 'react';

import { Button } from '@/components/ui/catalyst/button';

interface WizardNavigationProps {
  currentStep: number;
  totalSteps: number;
  isLoading?: boolean;
  canProceed?: boolean;
  onPrevious: () => void;
  onNext: () => void;
  nextButtonText?: string;
  previousButtonText?: string;
  showPrevious?: boolean;
}

export const WizardNavigation: React.FC<WizardNavigationProps> = ({
  currentStep,
  totalSteps,
  isLoading = false,
  canProceed = true,
  onPrevious,
  onNext,
  nextButtonText,
  previousButtonText = 'Previous',
  showPrevious = true,
}) => {
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === totalSteps - 1;

  const getNextButtonText = () => {
    if (nextButtonText) return nextButtonText;
    if (isLastStep) return isLoading ? 'Submitting...' : 'Submit';
    return 'Next';
  };

  return (
    <div className="flex justify-between items-center pt-8 border-t border-gray-200">
      <div>
        {!isFirstStep && showPrevious && (
          <Button
            type="button"
            plain
            onClick={onPrevious}
            disabled={isLoading}
            className="flex items-center px-4 py-2"
          >
            <ChevronLeftIcon className="h-4 w-4 mr-2" />
            {previousButtonText}
          </Button>
        )}
      </div>

      <div className="flex items-center space-x-4">
        {/* Step counter */}
        <span className="text-sm text-gray-500">
          Step {currentStep + 1} of {totalSteps}
        </span>

        <Button
          type="button"
          onClick={onNext}
          disabled={isLoading || !canProceed}
          className="flex items-center px-6 py-2 bg-blue-600 hover:bg-blue-700"
        >
          {getNextButtonText()}
          {!isLastStep && !isLoading && (
            <ChevronRightIcon className="h-4 w-4 ml-2" />
          )}
          {isLoading && (
            <svg
              className="animate-spin ml-2 h-4 w-4 text-white"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          )}
        </Button>
      </div>
    </div>
  );
};
